import { useMemo, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  collection,
  getDocs,
  query,
  startAfter,
  orderBy,
  limit,
  where,
} from "firebase/firestore";
import {
  Search,
  Crown,
  ChevronRight,
  Users as UsersIcon,
  Filter,
  X,
  Loader2,
} from "lucide-react";
import femaleNoImage from "../../Assets/femalenoimage.png";
import maleNoImage from "../../Assets/mennoimage.png";
import { db } from "../../firebase";
import AdminLayout from "../layout/AdminLayout";
import Combobox from "../../Components/Combobox";
import {
  isHiddenProfile,
  isMarriageSettledProfile,
} from "../../utils/profileVisibility";
import {
  isPendingVerification,
  isVerifiedProfile,
} from "../../utils/verification";

const adminTabs = [
  { key: "all", label: "All Users" },
  { key: "verified", label: "Verified" },
  { key: "pending", label: "Pending" },
  { key: "premium", label: "Premium" },
  { key: "hidden", label: "Hidden" },
  { key: "settled", label: "Settled" },
  { key: "blocked", label: "Blocked" },
  { key: "suspended", label: "Suspended" },
];

const PAGE_SIZE = 200;
const heightOptions = Array.from({ length: 25 }, (_, idx) => {
  const totalInches = 54 + idx;
  return `${Math.floor(totalInches / 12)}'${totalInches % 12}"`;
});
const maritalStatusOptions = ["Never Married", "Divorced", "Widowed", "Awaiting Divorce", "Marriage Settled"];
const religionOptions = ["Hindu", "Converted Christian"];
const casteOptions = ["Goud / Gowda", "Settibalija", "Ediga", "Srisayana", "Yatha"];
const rashiOptions = [
  "Mesha (మేషం)", "Vrishabha (వృషభం)", "Mithuna (మిథునం)", "Karka (కర్కాటకం)", "Simha (సింహం)",
  "Kanya (కన్య)", "Tula (తుల)", "Vrischika (వృశ్చికం)", "Dhanu (ధనుస్సు)", "Makara (మకరం)",
  "Kumbha (కుంభం)", "Meena (మీనం)",
];
const collegeTypeOptions = ["IIT", "NIT", "IIIT", "IIM", "AIIMS", "BITS", "Government University", "Government College", "Private University", "Private College", "Deemed University", "Foreign University", "Other"];
const annualIncomeOptions = ["Not Working", "Below 2 Lakhs", "2-5 Lakhs", "5-10 Lakhs", "10-15 Lakhs", "15-25 Lakhs", "25-50 Lakhs", "50 Lakhs - 1 Crore", "Above 1 Crore", "Prefer not to say"];
const familyFinancialStatusOptions = ["Lower Middle Class", "Middle Class", "Upper Middle Class", "Rich", "Prefer Not To Say"];
const membershipOptions = ["free", "premium"];
const statusOptions = ["active", "blocked", "suspended"];
const profileStatusOptions = ["Draft", "Pending Verification", "Verified", "Rejected", "Changes Requested"];
const defaultAdvFilters = {
  membership: "",
  status: "",
  profileStatus: "",
  gender: "",
  maritalStatus: "",
  height: "",
  religion: "",
  caste: "",
  rashi: "",
  collegeType: "",
  education: "",
  profession: "",
  annualIncome: "",
  familyFinancialStatus: "",
  residenceType: "",
  state: "",
  district: "",
};

const Users = () => {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advFilters, setAdvFilters] = useState(defaultAdvFilters);
  const navigate = useNavigate();
  const isAny = (v) => !v || v.toLowerCase() === "any";

  const sentinelRef = useRef(null);

  const {
    data: usersData,
    isLoading: usersLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error: usersError,
  } = useInfiniteQuery({
    queryKey: ["adminUsersList", filter, advFilters],
    queryFn: async ({ pageParam = null }) => {
      // Build server-side where clauses for supported filters
      const clauses = [];
      // Tab filters
      if (filter === "premium") clauses.push(where("membership", "==", "premium"));
      if (filter === "pending") clauses.push(where("profileStatus", "==", "pending"));
      if (filter === "verified") clauses.push(where("profileStatus", "==", "verified"));
      if (filter === "blocked") clauses.push(where("status", "==", "blocked"));
      if (filter === "suspended") clauses.push(where("status", "==", "suspended"));

      // Advanced equality filters (server-side where)
      if (advFilters?.membership) clauses.push(where("membership", "==", advFilters.membership));
      if (advFilters?.status) clauses.push(where("status", "==", advFilters.status));
      if (advFilters?.profileStatus) clauses.push(where("profileStatus", "==", advFilters.profileStatus));
      if (advFilters?.gender) clauses.push(where("gender", "==", advFilters.gender));
      if (advFilters?.maritalStatus) clauses.push(where("maritalStatus", "==", advFilters.maritalStatus));
      if (advFilters?.height) clauses.push(where("height", "==", advFilters.height));
      if (advFilters?.religion) clauses.push(where("religion", "==", advFilters.religion));
      if (advFilters?.caste) clauses.push(where("caste", "==", advFilters.caste));
      if (advFilters?.rashi) clauses.push(where("rashi", "==", advFilters.rashi));
      if (advFilters?.collegeType) clauses.push(where("collegeType", "==", advFilters.collegeType));
      if (advFilters?.education) clauses.push(where("education", "==", advFilters.education));
      if (advFilters?.profession) clauses.push(where("profession", "==", advFilters.profession));
      if (advFilters?.annualIncome) clauses.push(where("annualIncome", "==", advFilters.annualIncome));
      if (advFilters?.familyFinancialStatus) clauses.push(where("familyFinancialStatus", "==", advFilters.familyFinancialStatus));
      if (advFilters?.residenceType) clauses.push(where("residenceType", "==", advFilters.residenceType));
      if (advFilters?.state) clauses.push(where("state", "==", advFilters.state));
      if (advFilters?.district) clauses.push(where("district", "==", advFilters.district));

      // Try to run an ordered, paginated query. If Firestore requires a composite index, fallback to first-page un-ordered query.
      try {
        const baseArgs = [collection(db, "profiles"), ...clauses, orderBy("createdAt", "desc")];
        const q = pageParam
          ? query(...baseArgs, startAfter(pageParam), limit(PAGE_SIZE))
          : query(...baseArgs, limit(PAGE_SIZE));
        const snap = await getDocs(q);
        const docs = snap.docs;
        return {
          profiles: docs.map((d) => ({ id: d.id, ...d.data() })),
          nextCursor: docs.length === PAGE_SIZE ? docs[docs.length - 1] : null,
        };
      } catch (err) {
        // Index error fallback: only allow un-ordered first-page queries for compatibility
        if (pageParam) return { profiles: [], nextCursor: null };
        const fallbackQ = query(collection(db, "profiles"), ...clauses, limit(PAGE_SIZE));
        const snap = await getDocs(fallbackQ);
        const docs = snap.docs;
        return {
          profiles: docs.map((d) => ({ id: d.id, ...d.data() })),
          nextCursor: docs.length === PAGE_SIZE ? docs[docs.length - 1] : null,
        };
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 300000,
    cacheTime: 1800000,
    retry: 1,
  });

  const users = usersData?.pages.flatMap((p) => p.profiles) ?? [];
  const loading = usersLoading;
  const loadingMore = isFetchingNextPage;
  const hasMore = !!hasNextPage;

  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading || loadingMore) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) return;
      fetchNextPage().catch(() => {});
    }, { rootMargin: "300px 0px" });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, fetchNextPage]);

  useEffect(() => {
    if (!showAdvanced) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [showAdvanced]);

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const term = search.toLowerCase();
      const matchesSearch =
        (u.fullName || "").toLowerCase().includes(term) ||
        (u.presentResidence || "").toLowerCase().includes(term) ||
        (u.mobileNumber || "").includes(term) ||
        (u.kmProfileId || "").toLowerCase().includes(term);

      if (!matchesSearch) return false;

      // Tab filter
      switch (filter) {
        case "premium": if (u.membership !== "premium") return false; break;
        case "pending": if (!isPendingVerification(u)) return false; break;
        case "verified": if (!isVerifiedProfile(u)) return false; break;
        case "hidden": if (!isHiddenProfile(u)) return false; break;
        case "settled": if (!isMarriageSettledProfile(u)) return false; break;
        case "blocked": if ((u.status || "active") !== "blocked") return false; break;
        case "suspended": if ((u.status || "active") !== "suspended") return false; break;
        default: break;
      }

      // Advanced filters
      if (!isAny(advFilters.membership) && u.membership !== advFilters.membership) return false;
      if (!isAny(advFilters.status) && (u.status || "active") !== advFilters.status) return false;
      if (!isAny(advFilters.profileStatus) && (u.profileStatus || "") !== advFilters.profileStatus) return false;
      if (!isAny(advFilters.gender) && u.gender !== advFilters.gender) return false;
      if (!isAny(advFilters.maritalStatus) && u.maritalStatus !== advFilters.maritalStatus) return false;
      if (!isAny(advFilters.height) && u.height !== advFilters.height) return false;
      if (!isAny(advFilters.religion) && u.religion !== advFilters.religion) return false;
      if (!isAny(advFilters.caste) && u.caste !== advFilters.caste) return false;
      if (!isAny(advFilters.rashi) && u.rashi !== advFilters.rashi) return false;
      if (!isAny(advFilters.collegeType) && u.collegeType !== advFilters.collegeType) return false;
      if (!isAny(advFilters.education) && u.education !== advFilters.education) return false;
      if (!isAny(advFilters.profession) && u.profession !== advFilters.profession) return false;
      if (!isAny(advFilters.annualIncome) && u.annualIncome !== advFilters.annualIncome) return false;
      if (!isAny(advFilters.familyFinancialStatus) && u.familyFinancialStatus !== advFilters.familyFinancialStatus) return false;
      if (!isAny(advFilters.residenceType) && u.residenceType !== advFilters.residenceType) return false;
      if (!isAny(advFilters.state) && u.state !== advFilters.state) return false;
      if (!isAny(advFilters.district) && u.district !== advFilters.district) return false;

      return true;
    });
  }, [users, search, filter, advFilters]);

  // Derive unique options from data for advanced filters — state is filtered by residenceType
  const filterOptions = useMemo(() => {
    // For state options, only show states from users matching the current residenceType filter
    const residencePool = isAny(advFilters.residenceType)
      ? users
      : users.filter((u) => u.residenceType === advFilters.residenceType);
    const districtPool = isAny(advFilters.state)
      ? residencePool
      : residencePool.filter((u) => u.state === advFilters.state);

    return {
      membership: membershipOptions,
      status: statusOptions,
      profileStatus: profileStatusOptions,
      gender: [...new Set(users.map((u) => u.gender).filter(Boolean))].sort(),
      maritalStatus: maritalStatusOptions,
      height: [...new Set(users.map((u) => u.height).filter(Boolean))].sort(),
      religion: religionOptions,
      caste: casteOptions,
      rashi: rashiOptions,
      collegeType: collegeTypeOptions,
      education: [...new Set(users.map((u) => u.education).filter(Boolean))].sort(),
      profession: [...new Set(users.map((u) => u.profession).filter(Boolean))].sort(),
      annualIncome: annualIncomeOptions,
      familyFinancialStatus: familyFinancialStatusOptions,
      residenceType: [...new Set(users.map((u) => u.residenceType).filter(Boolean))].sort(),
      state: [...new Set(residencePool.map((u) => u.state).filter(Boolean))].sort(),
      district: [...new Set(districtPool.map((u) => u.district).filter(Boolean))].sort(),
    };
  }, [users, advFilters.residenceType, advFilters.state]);

  const advFilterCount = Object.values(advFilters).filter((v) => v && v.toLowerCase() !== "any").length;
  const closeAdvancedFilters = () => setShowAdvanced(false);
  const resetAdvancedFilters = () => setAdvFilters({ ...defaultAdvFilters });

  const counts = useMemo(() => ({
    all: users.length,
    hidden: users.filter((u) => isHiddenProfile(u)).length,
    settled: users.filter((u) => isMarriageSettledProfile(u)).length,
    blocked: users.filter((u) => (u.status || "active") === "blocked").length,
    suspended: users.filter((u) => (u.status || "active") === "suspended").length,
    premium: users.filter((u) => u.membership === "premium").length,
    pending: users.filter((u) => isPendingVerification(u)).length,
    verified: users.filter((u) => isVerifiedProfile(u)).length,
  }), [users]);

  return (
    <AdminLayout>
      {/* HEADER */}
      <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <p className="section-subtle">Manage</p>
          <h1 className="mt-1 font-display text-3xl font-bold text-ink-800">Users</h1>
          <p className="mt-1 text-[13.5px] text-ink-400">
            {filteredUsers.length} of {users.length} matrimony users
          </p>
        </div>
      </div>

      {/* FILTERS */}
      <div className="card p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="input-group flex-1">
            <Search size={17} className="text-ink-400" />
            <input
              placeholder="Search by name, Profile ID, residence, or mobile..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button onClick={() => setSearch("")} className="text-ink-400 hover:text-brand-600">
                <X size={14} />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className={`relative inline-flex items-center gap-2 rounded-2xl border px-3.5 py-2 text-[13px] font-semibold transition ${
              showAdvanced || advFilterCount
                ? "border-brand-600 bg-brand-50 text-brand-700"
                : "border-sand-200 bg-white text-ink-700 hover:border-brand-600 hover:text-brand-600"
            }`}
          >
            <Filter size={14} /> Advanced
            {advFilterCount > 0 && (
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-600 px-1.5 text-[10px] font-bold text-white">
                {advFilterCount}
              </span>
            )}
          </button>
        </div>

        <div className="-mx-1 mt-4 flex flex-nowrap gap-2 overflow-x-auto pb-1">
          {adminTabs.map((t) => {
            const active = filter === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setFilter(t.key)}
                className={`group inline-flex items-center gap-2 rounded-2xl px-3.5 py-1.5 text-[12.5px] font-semibold transition ${
                  active
                    ? "bg-brand-gradient text-white shadow-soft"
                    : "border border-sand-200 bg-white text-ink-700 hover:border-brand-600 hover:text-brand-600"
                }`}
              >
                {t.label}
                <span
                  className={`inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-[10.5px] ${
                    active ? "bg-white/25 text-white" : "bg-sand-100 text-ink-500 group-hover:bg-brand-50 group-hover:text-brand-700"
                  }`}
                >
                  {counts[t.key]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* TABLE / CARDS */}
      <div className="mt-5">
        {/* Desktop table */}
        <div className="hidden overflow-hidden rounded-3xl border border-sand-200 bg-white shadow-soft md:block">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px]">
              <thead>
                <tr className="border-b border-sand-200 bg-sand-50 text-left text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                  <th className="px-5 py-3.5">User</th>
                  <th className="px-5 py-3.5">Gender</th>
                  <th className="px-5 py-3.5">Profession</th>
                  <th className="px-5 py-3.5">Location</th>
                  <th className="px-5 py-3.5">Status</th>
                  <th className="px-5 py-3.5">Membership</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} className="border-b border-sand-100">
                      <td colSpan={6} className="px-5 py-3"><div className="skeleton h-12" /></td>
                    </tr>
                  ))
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center">
                      <UsersIcon className="mx-auto text-ink-300" />
                      <p className="mt-2 text-ink-400">No users found</p>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u) => (
                    <tr
                      key={u.id}
                      onClick={() => navigate(`/admin/users/${u.id}`)}
                      className="cursor-pointer border-b border-sand-100 transition hover:bg-sand-50"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          {u.hidePhotos || !u.imageUrls?.[0] ? (
                            <img
                              src={u.gender === "Female" ? femaleNoImage : maleNoImage}
                              alt={u.gender === "Female" ? "Female placeholder" : "Male placeholder"}
                              className="h-12 w-12 rounded-2xl object-cover object-center ring-1 ring-sand-200"
                            />
                          ) : (
                            <img
                              src={u.imageUrls[0]}
                              alt={u.fullName}
                              className="h-12 w-12 rounded-2xl object-cover object-center ring-1 ring-sand-200"
                            />
                          )}
                          <div className="min-w-0">
                            <h3 className="truncate font-semibold text-ink-800">{u.fullName || "No Name"}</h3>
                            <p className="text-[12px] text-ink-400">
                              {u.kmProfileId && <span className="mr-2 font-semibold text-brand-600">{u.kmProfileId}</span>}
                              {u.dateOfBirth
                                ? new Date(u.dateOfBirth).toLocaleDateString("en-GB").replaceAll("/", "-")
                                : "-"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-[13.5px] text-ink-700">{u.gender || "-"}</td>
                      <td className="px-5 py-4">
                        <p className="font-semibold text-ink-800">{u.profession || "-"}</p>
                        <p className="text-[12px] text-ink-400">{u.education || "-"}</p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="font-semibold text-ink-800">{u.residenceType || "-"}</p>
                        <p className="text-[12px] text-ink-400">{u.state || "-"}</p>
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                            (u.status || "active") === "blocked"
                              ? "bg-rose-50 text-rose-700"
                              : (u.status || "active") === "suspended"
                              ? "bg-amber-50 text-amber-700"
                              : "bg-emerald-50 text-emerald-700"
                          }`}
                        >
                          {u.status || "active"}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        {u.membership === "premium" ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-bold text-amber-700">
                            <Crown size={11} /> Premium
                          </span>
                        ) : (
                          <span className="rounded-full bg-sand-100 px-2.5 py-0.5 text-[11px] font-bold text-ink-500">
                            Free
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile cards */}
        <div className="grid gap-3 md:hidden">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-24 rounded-3xl" />)
            : filteredUsers.length === 0 ? (
              <div className="card p-10 text-center">
                <UsersIcon className="mx-auto text-ink-300" />
                <p className="mt-2 text-ink-400">No users found</p>
              </div>
            ) : filteredUsers.map((u) => (
              <motion.button
                key={u.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => navigate(`/admin/users/${u.id}`)}
                className="card flex items-center gap-3 p-3 text-left transition hover:shadow-elevated"
              >
                {u.hidePhotos || !u.imageUrls?.[0] ? (
                  <img
                    src={u.gender === "Female" ? femaleNoImage : maleNoImage}
                    alt={u.gender === "Female" ? "Female placeholder" : "Male placeholder"}
                    className="h-14 w-14 shrink-0 rounded-2xl object-cover object-center"
                  />
                ) : (
                  <img
                    src={u.imageUrls[0]}
                    alt={u.fullName}
                    className="h-14 w-14 shrink-0 rounded-2xl object-cover object-center"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <h3 className="truncate font-semibold text-ink-800">{u.fullName || "No Name"}</h3>
                      {u.kmProfileId && (
                        <p className="mt-1 truncate text-[12px] font-semibold text-brand-600">
                          {u.kmProfileId}
                        </p>
                      )}
                    </div>
                    {u.membership === "premium" && (
                      <Crown size={14} className="text-amber-500" />
                    )}
                  </div>
                  <p className="truncate text-[12px] text-ink-400">
                    {u.profession || "-"} • {u.state || "-"}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                      (u.status || "active") === "blocked" ? "bg-rose-50 text-rose-700" :
                      (u.status || "active") === "suspended" ? "bg-amber-50 text-amber-700" :
                      "bg-emerald-50 text-emerald-700"
                    }`}>
                      {u.status || "active"}
                    </span>
                    {isVerifiedProfile(u) && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Verified</span>}
                  </div>
                </div>
                <ChevronRight size={16} className="text-ink-300" />
              </motion.button>
          ))}
        </div>
      </div>

      {/* Load more sentinel + footer */}
      <div ref={sentinelRef} className="h-2" />
      {(hasMore || loadingMore) && (
        <div className="mt-4 flex items-center justify-center gap-3 py-4">
          {loadingMore ? (
            <div className="flex items-center gap-2 text-[13px] font-semibold text-brand-600">
              <Loader2 size={16} className="animate-spin" />
              Loading more users...
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fetchNextPage()}
              className="rounded-2xl border border-sand-200 bg-white px-5 py-2.5 text-[13px] font-semibold text-ink-700 transition hover:border-brand-600 hover:text-brand-600"
            >
              Load more
            </button>
          )}
        </div>
      )}

      <AnimatePresence>
        {showAdvanced && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-ink-900/45 backdrop-blur-sm"
            onClick={closeAdvancedFilters}
          >
            <motion.aside
              initial={{ x: 420 }}
              animate={{ x: 0 }}
              exit={{ x: 420 }}
              transition={{ type: "spring", stiffness: 320, damping: 32 }}
              onClick={(e) => e.stopPropagation()}
              className="ml-auto flex h-full w-full max-w-[560px] flex-col bg-white shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-sand-200 px-5 py-4 sm:px-6">
                <div>
                  <p className="section-subtle">Advanced filters</p>
                  <h2 className="mt-1 font-display text-2xl font-bold text-ink-800">Filter users</h2>
                </div>
                <button type="button" onClick={closeAdvancedFilters} className="btn-icon h-10 w-10">
                  <X size={16} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="field-label">Membership</label>
                    <Combobox value={advFilters.membership} onChange={(v) => setAdvFilters({ ...advFilters, membership: v })} options={filterOptions.membership} placeholder="Any" />
                  </div>
                  <div>
                    <label className="field-label">Account Status</label>
                    <Combobox value={advFilters.status} onChange={(v) => setAdvFilters({ ...advFilters, status: v })} options={filterOptions.status} placeholder="Any" />
                  </div>
                  <div>
                    <label className="field-label">Profile Status</label>
                    <Combobox value={advFilters.profileStatus} onChange={(v) => setAdvFilters({ ...advFilters, profileStatus: v })} options={filterOptions.profileStatus} placeholder="Any" />
                  </div>
                  <div>
                    <label className="field-label">Gender</label>
                    <Combobox value={advFilters.gender} onChange={(v) => setAdvFilters({ ...advFilters, gender: v })} options={filterOptions.gender} placeholder="Any" />
                  </div>
                  <div>
                    <label className="field-label">Marital Status</label>
                    <Combobox value={advFilters.maritalStatus} onChange={(v) => setAdvFilters({ ...advFilters, maritalStatus: v })} options={filterOptions.maritalStatus} placeholder="Any" />
                  </div>
                  <div>
                    <label className="field-label">Height</label>
                    <Combobox value={advFilters.height} onChange={(v) => setAdvFilters({ ...advFilters, height: v })} options={filterOptions.height} placeholder="Any" searchPlaceholder="Search height..." />
                  </div>
                  <div>
                    <label className="field-label">Religion</label>
                    <Combobox value={advFilters.religion} onChange={(v) => setAdvFilters({ ...advFilters, religion: v })} options={filterOptions.religion} placeholder="Any" />
                  </div>
                  <div>
                    <label className="field-label">Caste</label>
                    <Combobox value={advFilters.caste} onChange={(v) => setAdvFilters({ ...advFilters, caste: v })} options={filterOptions.caste} placeholder="Any" />
                  </div>
                  <div>
                    <label className="field-label">Rashi</label>
                    <Combobox value={advFilters.rashi} onChange={(v) => setAdvFilters({ ...advFilters, rashi: v })} options={filterOptions.rashi} placeholder="Any" searchPlaceholder="Search rashi..." />
                  </div>
                  <div>
                    <label className="field-label">College Type</label>
                    <Combobox value={advFilters.collegeType} onChange={(v) => setAdvFilters({ ...advFilters, collegeType: v })} options={filterOptions.collegeType} placeholder="Any" searchPlaceholder="Search type..." />
                  </div>
                  <div>
                    <label className="field-label">Education</label>
                    <Combobox value={advFilters.education} onChange={(v) => setAdvFilters({ ...advFilters, education: v })} options={filterOptions.education} placeholder="Any" searchPlaceholder="Search education..." />
                  </div>
                  <div>
                    <label className="field-label">Profession</label>
                    <Combobox value={advFilters.profession} onChange={(v) => setAdvFilters({ ...advFilters, profession: v })} options={filterOptions.profession} placeholder="Any" searchPlaceholder="Search profession..." />
                  </div>
                  <div>
                    <label className="field-label">Annual Income</label>
                    <Combobox value={advFilters.annualIncome} onChange={(v) => setAdvFilters({ ...advFilters, annualIncome: v })} options={filterOptions.annualIncome} placeholder="Any" />
                  </div>
                  <div>
                    <label className="field-label">Family Status</label>
                    <Combobox value={advFilters.familyFinancialStatus} onChange={(v) => setAdvFilters({ ...advFilters, familyFinancialStatus: v })} options={filterOptions.familyFinancialStatus} placeholder="Any" />
                  </div>
                  <div>
                    <label className="field-label">Residence Type</label>
                    <Combobox value={advFilters.residenceType} onChange={(v) => setAdvFilters({ ...advFilters, residenceType: v, state: "", district: "" })} options={filterOptions.residenceType} placeholder="Any" searchPlaceholder="Search type..." />
                  </div>
                  <div>
                    <label className="field-label">State</label>
                    <Combobox value={advFilters.state} onChange={(v) => setAdvFilters({ ...advFilters, state: v, district: "" })} options={filterOptions.state} placeholder="Any" searchPlaceholder="Search state..." />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="field-label">District</label>
                    <Combobox value={advFilters.district} onChange={(v) => setAdvFilters({ ...advFilters, district: v })} options={filterOptions.district} placeholder="Any" searchPlaceholder="Search district..." />
                  </div>
                </div>
              </div>

              <div className="border-t border-sand-200 px-5 py-4 sm:px-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
                  <button
                    type="button"
                    onClick={resetAdvancedFilters}
                    className="btn-ghost w-full sm:w-auto"
                  >
                    Clear all filters
                  </button>
                  <div className="flex gap-3">
                    <button type="button" onClick={closeAdvancedFilters} className="btn-ghost flex-1 sm:flex-none">
                      Close
                    </button>
                    <button type="button" onClick={closeAdvancedFilters} className="btn-primary flex-1 sm:flex-none">
                      Apply filters
                    </button>
                  </div>
                </div>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
};

export default Users;
